import React, { useEffect, useMemo, useState } from "react";
import "./OperadorContextManager.css";

import { ListarSimples as ListarUsuariosSimples } from "../../../Services/Usuario";

export default function OperadorContextManager({
  aberto,
  onFechar,
  session,
  selecionadoId,
  onAplicar,
  conversas = []
}) {
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  const [usuarios, setUsuarios] = useState([]);
  const [filtro, setFiltro] = useState("");

  const [selecionadoLocal, setSelecionadoLocal] = useState(0);

  const [somenteComConversas, setSomenteComConversas] = useState(false);

  const reset = () => {
    setErro(null);
    setFiltro("");
    setSomenteComConversas(false);
    setSelecionadoLocal(selecionadoId || session?.usuarioId || 0);
  };

  const carregar = async () => {
    setLoading(true);
    setErro(null);

    try {
      const filtroUsuario = {
        usuarioNome: "",
        usuarioLogin: "",
        grupoUsuarioId: 0, // se quiser filtrar somente operadores, ajuste aqui
      };

      const resp = await ListarUsuariosSimples(filtroUsuario);

      if (!resp?.success) {
        setUsuarios([]);
        setErro(resp?.message || "Não foi possível carregar operadores.");
        setLoading(false);
        return;
      }

      const list = Array.isArray(resp.usuarios) ? resp.usuarios.slice() : [];

      const normalizados = list
        .map((u) => ({
          usuarioId: Number(u.usuarioId ?? u.id ?? u.usuario_id ?? 0),
          usuarioNome: String(
            u.usuarioNome ?? u.nome ?? u.usuario_nome ?? ""
          ).trim(),
          usuarioLogin: String(
            u.usuarioLogin ?? u.login ?? u.usuario_login ?? ""
          ).trim(),
          grupoUsuarioId: Number(u.grupoUsuarioId ?? u.grupo_usuario_id ?? 0),
        }))
        .filter((u) => u.usuarioId > 0 && (u.usuarioNome || u.usuarioLogin));

      normalizados.sort((a, b) =>
        String(a.usuarioNome || a.usuarioLogin).localeCompare(
          String(b.usuarioNome || b.usuarioLogin),
          "pt-BR"
        )
      );

      setUsuarios(normalizados);
      setLoading(false);
    } catch (e) {
      console.error("OperadorContextManager :: erro ao listar usuários:", e);
      setUsuarios([]);
      setErro("Não foi possível carregar operadores.");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!aberto) return;
    reset();
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    setSelecionadoLocal(selecionadoId || session?.usuarioId || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecionadoId]);

  const normalizarTexto = (txt) =>
    (txt || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const usuariosFiltrados = useMemo(() => {
    const term = normalizarTexto(filtro);
    if (!term) return usuarios;

    return (usuarios || []).filter((u) => {
      const nome = normalizarTexto(u.usuarioNome);
      const login = normalizarTexto(u.usuarioLogin);
      const id = normalizarTexto(String(u.usuarioId));
      return nome.includes(term) || login.includes(term) || id.includes(term);
    });
  }, [usuarios, filtro]);


  const contagemConversasPorUsuarioId = useMemo(() => {
    const map = Object.create(null);

    const extrairUsuarioId = (c) =>
      Number(
        c.ultimoOperadorUsuarioId
      );

    (conversas || []).forEach((c) => {
      const uid = extrairUsuarioId(c);
      if (!uid) return;
      map[uid] = (map[uid] || 0) + 1;
    });

    return map;
  }, [conversas]);

  const usuariosExibidos = useMemo(() => {
    const base = usuariosFiltrados || [];
    if (!somenteComConversas) return base;

    return base.filter((u) => Number(contagemConversasPorUsuarioId[u.usuarioId] || 0) > 0);
  }, [usuariosFiltrados, somenteComConversas, contagemConversasPorUsuarioId]);

  const qtdOperadoresComConversas = useMemo(() => {
    return (usuarios || []).reduce((acc, u) => {
      const qtd = Number(contagemConversasPorUsuarioId[u.usuarioId] || 0);
      return acc + (qtd > 0 ? 1 : 0);
    }, 0);
  }, [usuarios, contagemConversasPorUsuarioId]);


  const aplicar = () => {
    const id = Number(selecionadoLocal || 0);

    const usuarioSelecionado =
      (usuarios || []).find((u) => Number(u.usuarioId) === id) || null;

    if (onAplicar) {
      onAplicar(
        usuarioSelecionado || {
          usuarioId: id,
          usuarioNome: session?.usuarioNome || "",
          usuarioLogin: session?.usuarioLogin || "",
        }
      );
    }

    onFechar();
  };

  if (!aberto) return null;

  const nomeLogado =
    session?.usuarioNome || `Usuário ${session?.usuarioId || ""}`;

  return (
    <div className="operador-context-modal-overlay">
      <div className="operador-context-modal">
        {/* HEADER */}
        <div className="operador-context-modal-header">
          <div className="operador-context-title">
            Operador do Contexto
            <span className="operador-context-subtitle">
              {" "}
              • Logado: {nomeLogado}
            </span>
          </div>

          <button
            type="button"
            className="operador-context-close"
            onClick={onFechar}
            aria-label="Fechar"
            title="Fechar"
          >
            ✕
          </button>
        </div>

        {/* BODY */}
        <div className="operador-context-modal-body">
          {loading ? (
            <div>Carregando operadores...</div>
          ) : (
            <>
              <div className="operador-context-toolbar">
                <input
                  type="text"
                  className="operador-context-search"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  placeholder="Pesquisar operador (nome/login/id)..."
                  autoComplete="off"
                />
                <button
                  type="button"
                  className={`operador-context-filter-btn ${somenteComConversas ? "active" : ""}`}
                  onClick={() => setSomenteComConversas((v) => !v)}
                  title="Filtrar: somente operadores com conversas"
                >
                  Somente com conversas
                  <span className="operador-context-filter-badge">{qtdOperadoresComConversas}</span>
                </button>

              </div>

              {erro && <div className="operador-context-erro">{erro}</div>}

              {!erro && usuariosExibidos.length === 0 ? (
                <div className="operador-context-empty">
                  {somenteComConversas
                    ? "Nenhum operador com conversas no momento."
                    : "Nenhum operador encontrado."}
                </div>
              ) : (
                <div className="operador-context-list">
                  {usuariosExibidos.map((u) => {
                    const checked =
                      Number(selecionadoLocal) === Number(u.usuarioId);

                    return (
                      <div
                        key={u.usuarioId}
                        className={`operador-context-item ${checked ? "selected" : ""
                          }`}
                        onClick={() => setSelecionadoLocal(u.usuarioId)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelecionadoLocal(u.usuarioId);
                          }
                        }}
                        title="Selecionar operador"
                      >
                        <label
                          className="operador-context-item-left"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="radio"
                            name="operador-context"
                            checked={checked}
                            onChange={() => setSelecionadoLocal(u.usuarioId)}
                          />

                          <div className="operador-context-item-text">
                            <div className="operador-context-item-name">
                              {u.usuarioNome || "(Sem nome)"}
                            </div>
                            <div className="operador-context-item-login">
                              {(() => {
                                const qtd = Number(contagemConversasPorUsuarioId[u.usuarioId] || 0);
                                const loginText = u.usuarioLogin ? u.usuarioLogin : `ID: ${u.usuarioId}`;
                                return (
                                  <>
                                    {loginText}
                                    <span className="operador-context-item-count">
                                      {" "}
                                      • {qtd} conversa{qtd === 1 ? "" : "s"}
                                    </span>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </label>

                        {checked && (
                          <span
                            className="operador-context-item-check"
                            aria-hidden="true"
                          >
                            ✓
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="operador-context-modal-footer">
          <button
            type="button"
            className="operador-context-btn-secondary"
            onClick={onFechar}
          >
            Cancelar
          </button>

          <button
            type="button"
            className="operador-context-btn-primary"
            onClick={aplicar}
            disabled={loading || Number(selecionadoLocal || 0) <= 0}
            title="Aplicar operador selecionado"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}
